# Cookie Policy

Last updated: 17th December 2025

## Overview

This Cookie Policy supplements the Flowstep Privacy Policy and explains how **FlowstepDesign OÜ** (“Flowstep”, “we”, “us”) uses cookies and similar technologies (“**Cookies**”) on the Flowstep platform available at [**www.flowstep.ai**](http://www.flowstep.ai/) (the “**Platform**”).

This Cookie Policy is adopted in accordance with applicable data protection law, including the **General Data Protection Regulation (EU) 2016/679 (GDPR)** and national rules implementing the **EU ePrivacy Directive**.

Cookies are used to ensure the proper functioning of the Platform, to analyse usage, and, where applicable, for marketing purposes. Where required by law, Cookies are used only with the User’s consent.

Flowstep may update this Cookie Policy from time to time. The latest version is always available on the Platform.

If you have questions about this Cookie Policy, you may contact us at [hello@flowstep.ai](mailto:hello@flowstep.ai).

---

## 1. What are cookies?

Cookies are small text files stored on your device (computer, smartphone, tablet) when you visit a website. Cookies allow the website to recognise your device and store information about your preferences or past actions.

Cookies may be:

- **Session cookies**, which are deleted when you close your browser, or
- **Persistent cookies**, which remain on your device for a defined period or until deleted.

---

## 2. Types of cookies used by Flowstep

Flowstep uses the following categories of cookies:

### a. Strictly necessary cookies

These cookies are essential for the operation of the Platform and enable core functionality such as user authentication, security, and session management. The Platform cannot function properly without these cookies.

These cookies do not require consent under applicable law.

### b. Analytics and performance cookies

These cookies allow Flowstep to understand how Users interact with the Platform, measure traffic, and improve performance and usability.

Analytics cookies may also be used to analyse product usage within the logged-in Platform.

Analytics data is collected in aggregated or pseudonymised form and is used solely for improving the Platform.

Where required by law, these cookies are used only with the User’s consent.

### c. Marketing cookies

Marketing cookies may be used to support advertising and measure the effectiveness of marketing campaigns.

At present, Flowstep does not actively use advertising or behavioural targeting cookies. If such cookies are introduced in the future, they will be used only where permitted by law and subject to User consent.

---

## 3. Cookies currently in use

## Strictly necessary cookies

| Provider | Name                     | Expiration                                             | Purpose                                                |
| --- | --- | --- | --- |
| Flowstep | flowstep_refresh_token   | Up to the maximum period defined by Flowstep           | Refreshes and maintains the authenticated user session |
| Flowstep | flowstep_cookies_consent | Up to the maximum period defined by Flowstep           | Stores the User’s cookie consent preferences           |
| Flowstep | sb-apollo-auth-token.0   | Session / up to the maximum period defined by Flowstep | Enables authentication and API communication           |
| Flowstep | sb-apollo-auth-token.1   | Session / up to the maximum period defined by Flowstep | Enables authentication and API communication           |

---

## Analytics cookies

| Provider               | Name                 | Expiration                                       | Purpose                                         |
| --- | --- | --- | --- |
| Google Analytics (GA4) | _ga | Up to the maximum period defined by the provider | Distinguishes users and analyses platform usage |
| Google Analytics (GA4) | *ga** | Up to the maximum period defined by the provider | Stores and counts page views and interactions |
| Mixpanel | flowstep_mixpanel_* | Up to the maximum period defined by the provider | Measures product and platform usage patterns |

Google Analytics (GA4) and Mixpanel are used with their default configurations. These analytics cookies are only set after the User has provided consent via the cookie banner. Data collected through these tools is used to understand usage patterns and improve the Platform.

---

## 4. Consent and cookie management

When you first visit the Platform, you are presented with a cookie banner allowing you to manage your cookie preferences. Where required by law, non-essential cookies are placed only after you provide consent.

You may withdraw or modify your consent at any time via the cookie banner or by adjusting your browser settings.

In the event of any inconsistency between this Cookie Policy and the cookie banner, the cookie banner settings take precedence.

Please note that disabling certain cookies may affect the functionality of the Platform.

---

## 5. Managing cookies through your browser

Most web browsers allow you to control cookies through their settings. You can delete existing cookies or block future cookies via your browser.

Instructions for managing cookies can usually be found in your browser’s help section. General information about managing cookies is also available at [**www.allaboutcookies.org**](http://www.allaboutcookies.org/).

Flowstep is not responsible for your browser settings.

---

## 6. Changes to this Cookie Policy

Flowstep may update this Cookie Policy to reflect changes in the use of cookies or legal requirements. Material changes will be communicated via the Platform or, where appropriate, via the cookie banner.
